import type { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { APP_CONFIG_TOKEN, type AppConfig } from '@quipex/core/config';

/**
 * Maps Orval placeholder prefixes to AppConfig base URL keys.
 * Each Orval config sets `baseUrl: '/@<name>'` so generated URLs
 * start with that prefix. This interceptor resolves them to the
 * runtime base URL before MSAL and other interceptors see the request.
 *
 * When adding a new API, add a `'/@<name>': '<configKey>'` entry here
 * and set `baseUrl: '/@<name>'` in the corresponding orval.config.ts entry.
 * See src/orval/README.md for details.
 */
const API_PREFIX_MAP: Record<string, keyof AppConfig> = {
    '/@core': 'apiBase',
    '/@functions': 'apiFunctionBase',
    '/@ai': 'apiAIBase',
};

export const apiBaseUrlInterceptor: HttpInterceptorFn = (req, next) => {
    const appConfig = inject(APP_CONFIG_TOKEN);

    for (const [prefix, configKey] of Object.entries(API_PREFIX_MAP)) {
        if (req.url.startsWith(prefix)) {
            const baseUrl = appConfig[configKey] as string;

            if (typeof baseUrl !== 'string' || baseUrl.trim() === '') {
                throw new Error(
                    `apiBaseUrlInterceptor: AppConfig['${String(configKey)}'] is undefined or empty. ` +
                        `Cannot resolve prefix '${prefix}' for request URL '${req.url}'.`
                );
            }

            req = req.clone({ url: req.url.replace(prefix, baseUrl) });
            break;
        }
    }

    return next(req);
};
